import { useState } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import { Chevron, Popover } from "./Popover";
import { toBeijing } from "./beijing";
import { PRESETS, parseBeijing, type DateRangeValue } from "./dateRangeValue";
import type { OpenControl } from "./hooks";

// 日历里的 Date 都是"平移后"的值（toBeijing），用 getUTC* 读到的就是北京时间的年月日；只有应用时经 parseBeijing 换回真实时刻（D174）。
const DOW = ["一", "二", "三", "四", "五", "六", "日"];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
const p2 = (x: number) => (x < 10 ? "0" : "") + x;
const ymd = (d: Date) => `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
const hms = (d: Date) => `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}`;
const dayOf = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const monthOf = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const addMonths = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && a.getTime() === b.getTime();

interface CalMonthProps {
  month: Date; start: Date | null; end: Date | null; today: Date; maxDate: Date | null;
  onPick: (d: Date) => void; onHover: (d: Date | null) => void; onPrev?: () => void; onNext?: () => void;
}

function CalMonth({ month, start, end, today, maxDate, onPick, onHover, onPrev, onNext }: CalMonthProps) {
  const first = (month.getUTCDay() + 6) % 7; // 周一开头
  const days = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(<span key={`b${i}`} />);
  for (let d = 1; d <= days; d++) {
    const date = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), d));
    const isStart = sameDay(date, start), isEnd = sameDay(date, end);
    const inRange = !!start && !!end && date > start && date < end;
    const edgeInRange = (isStart || isEnd) && !!start && !!end && !sameDay(start, end);
    cells.push(
      <button key={d} type="button" disabled={!!maxDate && date > maxDate} aria-label={ymd(date)} aria-pressed={isStart || isEnd || undefined}
        className={cx("ax-cal-day", isStart && "start", isEnd && "end", (inRange || edgeInRange) && "in-range", sameDay(date, today) && "today")}
        onClick={() => onPick(date)} onMouseEnter={() => onHover(date)}>{d}</button>,
    );
  }
  return (
    <div className="ax-cal">
      <div className="ax-cal-head">
        {onPrev ? <button type="button" className="ax-cal-nav" aria-label="上一月" onClick={onPrev}>‹</button> : <span className="ph" />}
        <span>{month.getUTCFullYear()}年{month.getUTCMonth() + 1}月</span>
        {onNext ? <button type="button" className="ax-cal-nav" aria-label="下一月" onClick={onNext}>›</button> : <span className="ph" />}
      </div>
      <div className="ax-cal-grid" role="grid" onMouseLeave={() => onHover(null)}>
        {DOW.map((n) => <span key={n} className="ax-cal-dow">{n}</span>)}
        {cells}
      </div>
    </div>
  );
}

export interface DateRangeProps extends OpenControl {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  allowFuture?: boolean;
  size?: "sm";
  align?: "start" | "end";
  disabled?: boolean;
  className?: string;
}

/** 预设在前，双月日历选起止日期，时间精确到秒，全程北京时间（D174）。 */
export function DateRange({ value, onChange, allowFuture, size, align, disabled, className, ...ctl }: DateRangeProps) {
  const shifted = (s: string) => { const d = parseBeijing(s); return d ? toBeijing(d) : null; };
  const initFrom = "from" in value ? shifted(value.from) : null;
  const initTo = "to" in value ? shifted(value.to) : null;
  const today = dayOf(toBeijing(new Date()));
  const [start, setStart] = useState<Date | null>(initFrom ? dayOf(initFrom) : null);
  const [end, setEnd] = useState<Date | null>(initTo ? dayOf(initTo) : null);
  const [fromT, setFromT] = useState(initFrom ? hms(initFrom) : "00:00:00");
  const [toT, setToT] = useState(initTo ? hms(initTo) : "23:59:59");
  const [hover, setHover] = useState<Date | null>(null);
  const [view, setView] = useState(monthOf(initFrom ?? addMonths(today, -1)));

  const pick = (d: Date) => {
    if (!start || end) { setStart(d); setEnd(null); }
    else if (d < start) setStart(d);
    else setEnd(d);
  };
  const previewEnd = end ?? (start && hover && hover > start ? hover : null);
  const fromOk = TIME_RE.test(fromT), toOk = TIME_RE.test(toT);
  const fromStr = start ? `${ymd(start)} ${fromT}` : "", toStr = end ? `${ymd(end)} ${toT}` : "";
  const valid = !!start && !!end && fromOk && toOk && (parseBeijing(fromStr)?.getTime() ?? 0) < (parseBeijing(toStr)?.getTime() ?? 0);
  const hint = !start ? "点选开始日期" : !end ? "点选结束日期" : !fromOk || !toOk ? "时间格式 HH:mm:ss" : !valid ? "结束必须晚于开始" : "";
  const label = "preset" in value ? (PRESETS.find((p) => p[0] === value.preset)?.[1] ?? value.preset) : `${value.from} → ${value.to}`;
  const maxDate = allowFuture ? null : today;

  const trigger = (
    <button type="button" className={cx("ax-combobox-trigger ax-daterange-trigger", size === "sm" && "sm")} disabled={disabled}>
      <svg className="ax-daterange-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" aria-hidden><rect x={2} y={3} width={12} height={11} rx={1.5} /><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" /></svg>
      <span className={"preset" in value ? undefined : "ax-mono"}>{label}</span>
      <Chevron />
    </button>
  );

  // 浮层外壳（开关、外部点击与 Escape 关闭）复用 Popover，这里只负责面板内容。
  return (
    <Popover trigger={trigger} align={align} className={className} {...ctl}>
      {(close) => (
        <div className="ax-daterange-panel" aria-label="选择时间范围">
          <div className="ax-daterange-presets">
            {PRESETS.map((p) => (
              <button key={p[0]} type="button" className={cx("ax-menu-item", "preset" in value && value.preset === p[0] && "is-active")} onClick={() => { onChange({ preset: p[0] }); close(); }}>{p[1]}</button>
            ))}
          </div>
          <div className="ax-cal-area">
            <div className="ax-cal-months">
              <CalMonth month={view} start={start} end={previewEnd} today={today} maxDate={maxDate} onPick={pick} onHover={setHover} onPrev={() => setView(addMonths(view, -1))} />
              <CalMonth month={addMonths(view, 1)} start={start} end={previewEnd} today={today} maxDate={maxDate} onPick={pick} onHover={setHover} onNext={() => setView(addMonths(view, 1))} />
            </div>
            <div className="ax-cal-times">
              <label className="ax-cal-time"><span className="lbl">开始时间</span><input className="ax-input ax-input-sm ax-input-mono" value={fromT} aria-invalid={fromOk ? undefined : true} onChange={(e) => setFromT(e.target.value)} /></label>
              <label className="ax-cal-time"><span className="lbl">结束时间</span><input className="ax-input ax-input-sm ax-input-mono" value={toT} aria-invalid={toOk ? undefined : true} onChange={(e) => setToT(e.target.value)} /></label>
            </div>
            <div className="ax-cal-foot">
              <span>{hint}</span>
              <span className="ax-row">
                <Button variant="ghost" size="sm" onClick={() => { setStart(null); setEnd(null); }}>清除</Button>
                <Button variant="primary" size="sm" disabled={!valid} onClick={() => { onChange({ from: fromStr, to: toStr }); close(); }}>应用</Button>
              </span>
            </div>
          </div>
        </div>
      )}
    </Popover>
  );
}
